import React, { useContext, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDollarSign, faList } from "@fortawesome/free-solid-svg-icons";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import NumberFormat from "react-number-format";
import { checkUserToken } from "../../helpers/api/auth";
import { GetMyBalance } from "../../helpers/api/finance";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import { CurrencyContext } from "../../context/Context";
import { PreloadState } from "../UI/preloading/PreloadState";
import { AccountStatement } from "./ReceivableProperties";
import Background from "../common/Background";
import corner1 from "../../assets/img/illustrations/corner-1.png";

const PropertyDebtsToPay = () => {
  const { token } = useSelector((state) => state.auth.user);
  const { baseCurrency, currency } = useContext(CurrencyContext);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState(false);
  const [balance, setBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [balanceError, setBalanceError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let isMounted = true;
    setProfileLoading(true);
    setProfileError(false);
    checkUserToken(token)
      .then((response) => {
        const condominiumId =
          response?.condominium?.condominium_id ??
          response?.condominium?.id ??
          response?.condominium_id;
        if (!condominiumId) {
          throw new Error("No se pudo identificar el condominio del propietario.");
        }
        if (isMounted) setProfile(response);
      })
      .catch((error) => {
        if (isMounted) {
          setProfileError(true);
          handleErrorMessage(error);
        }
      })
      .finally(() => {
        if (isMounted) setProfileLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [token, reloadKey]);

  useEffect(() => {
    let isMounted = true;
    setBalanceLoading(true);
    setBalanceError(false);
    GetMyBalance()
      .then(({ data }) => {
        if (isMounted) setBalance(data.balance);
      })
      .catch((error) => {
        if (isMounted) {
          setBalanceError(true);
          handleErrorMessage(error);
        }
      })
      .finally(() => {
        if (isMounted) setBalanceLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [token, reloadKey]);

  if (profileLoading) return <PreloadState loading />;

  if (profileError) {
    return (
      <Alert color="danger">
        No se pudo cargar tu estado de cuenta.
        <Button color="link" onClick={() => setReloadKey((key) => key + 1)}>
          Reintentar
        </Button>
      </Alert>
    );
  }

  return (
    <AccountStatement ownerView ownerProfile={profile}>
      <Card className="mb-3 shadow-none border d-print-none">
        <Background image={corner1} className="bg-card" />
        <CardBody className="position-relative d-flex flex-wrap align-items-center justify-content-between">
          <div className="mr-3 mb-2">
            <h6 className="fs-0">Saldo a favor</h6>
            {balanceError ? (
              <span className="text-danger">No se pudo cargar el saldo a favor.</span>
            ) : (
              <PreloadState loading={balanceLoading} loadingClass="p-0">
                <span className="fs-1">
                  <NumberFormat
                    value={balance}
                    prefix={`${baseCurrency.symbol} `}
                    thousandSeparator
                    decimalScale={2}
                    fixedDecimalScale
                    displayType="text"
                  />
                  {currency?.currency?.iso && (
                    <NumberFormat
                      value={Number(balance) * Number(currency.value || 0)}
                      prefix={` | ${currency.currency.iso} `}
                      thousandSeparator
                      decimalScale={2}
                      fixedDecimalScale
                      displayType="text"
                    />
                  )}
                </span>
              </PreloadState>
            )}
            <p className="text-success fs-0 mt-2 mb-0">
              **Utiliza tu saldo para estar al día con tus pagos.
            </p>
          </div>
          <UncontrolledDropdown>
            <DropdownToggle caret color="falcon-default">
              Saldos a favor
            </DropdownToggle>
            <DropdownMenu right>
              <DropdownItem header>Opciones</DropdownItem>
              <DropdownItem tag={Link} to="/dashboard/my/finance/advance/add">
                <FontAwesomeIcon icon={faDollarSign} className="mr-2" />
                Registrar saldo a favor
              </DropdownItem>
              <DropdownItem tag={Link} to="/dashboard/my/finance/advance">
                <FontAwesomeIcon icon={faList} className="mr-2" />
                Ver mis saldos a favor
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </CardBody>
      </Card>
    </AccountStatement>
  );
};

export default PropertyDebtsToPay;
